import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Navbar.css';
import NotificationCenter from './NotificationCenter.jsx';
import { useAuth } from '../context/AuthContext.js';

function DropdownMenu({ label, icon, items, currentPath }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isActive = items.some(item => currentPath.startsWith(item.path));

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <li className={`nav-dropdown-wrapper ${isActive ? 'active' : ''}`} ref={ref}>
      <button className={`nav-dropdown-trigger ${isActive ? 'active' : ''}`} onClick={() => setOpen(o => !o)}>
        <span>{icon}</span> {label} <span className="nav-caret">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="nav-dropdown-menu">
          {items.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-dropdown-item ${currentPath.startsWith(item.path) ? 'active' : ''}`}
              onClick={() => setOpen(false)}
            >
              <span className="dd-icon">{item.icon}</span> {item.label}
            </Link>
          ))}
        </div>
      )}
    </li>
  );
}

// ── Staff information architecture (operations-first; athlete-development folded
// into one "Development" group since it's the supplemental differentiator) ──
const ESSENTIALS = [
  { label: 'Today', path: '/today', icon: '🏠' },
  { label: 'Roster', path: '/athletes', icon: '👥' },
  { label: 'Schedule', path: '/calendar', icon: '🗓' },
  { label: 'Communication', path: '/communication', icon: '💬' },
];
const OPERATIONS = { label: 'Operations', icon: '⚙️', items: [
  // Ops & Compliance + Roster Intake merged into the main-nav Roster (/athletes):
  // status columns, filter tiles and bulk remind/invite now live there, and each
  // athlete clicks through to their Athlete Card.
  { label: 'Registration Kiosk', path: '/kiosk', icon: '📝' },
  { label: 'Classes & Attendance', path: '/classes-hub', icon: '📚' },
  { label: 'Practice Logistics', path: '/practice', icon: '📋' },
  // Meet Coordination removed from nav — meets are created via the Calendar, which
  // still deep-links into /meet-coordination for meet details (route kept live).
  { label: 'Staff Schedule & Time', path: '/staff', icon: '📅' },
  // Practice Analytics moved under Development, merged into the Performance hub.
] };
const BUSINESS = { label: 'Business', icon: '💵', items: [
  // Billing, Payment Plans and Reports were merged into one hub with internal
  // tabs (Overview / Invoices / Plans & Recurring / Accounting). Coaches see the
  // Invoices tab only; admins see everything.
  { label: 'Billing & Finance', path: '/billing', icon: '💵' },
  { label: 'Waivers & Forms', path: '/waivers', icon: '📝' },
  { label: 'Insurance & Liability', path: '/insurance', icon: '🛡️' },
  { label: 'Medical Profiles', path: '/medical-profile', icon: '🏥' },
] };
const DEVELOPMENT = { label: 'Development', icon: '📈', items: [
  { label: 'Video Analysis', path: '/video', icon: '🎥' },
  { label: 'Goals & Progress', path: '/goals/review', icon: '🎯' },
  { label: 'Performance', path: '/performance', icon: '📊' },
  { label: 'Wellness', path: '/wellness', icon: '🧘' },
  // Skill Tracker hidden for beta — bring back once it's fed by the ML pipeline
  // (auto skill detection). Route /skills + component are intact.
] };
const ADMIN = { label: 'Admin', icon: '⚙️', items: [
  { label: 'User Management', path: '/admin/users', icon: '👤' },
  { label: 'Parents', path: '/admin/parents', icon: '👪' },
  { label: 'Athlete Accounts', path: '/admin/athlete-accounts', icon: '🧑‍🎓' },
  { label: 'My Profile', path: '/profile', icon: '🪪' },
] };
// SUPERADMIN / RMT ONLY — model development + labeling. Hidden from gym coaches
// and gym admins; this is the global model-building toolset.
const MODEL_LAB = { label: 'Model Lab', icon: '🤖', items: [
  { label: 'Landing Review (label)', path: '/landing-review', icon: '📹' },
  { label: 'Batch Classify (label)', path: '/batch-classification', icon: '⚡' },
  { label: 'Verify Labels', path: '/verify-labels', icon: '✅' },
  { label: 'Model Monitoring', path: '/model-monitoring', icon: '📈' },
] };

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const userRole = user?.role;
  const teamName = user?.teamName || 'AMS';
  const isStaff = userRole === 'admin' || userRole === 'coach';
  const path = location.pathname;

  const handleLogout = () => { logout(); navigate('/login'); };
  const close = () => setMobileOpen(false);
  const mlink = (it) => <Link key={it.path} to={it.path} onClick={close}>{it.icon} {it.label}</Link>;
  // Hide admin-only items from coaches so nav never leads to a blocked page
  const forRole = (items) => items.filter(it => !it.adminOnly || userRole === 'admin');

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-brand">🏃 {teamName}</Link>

        {/* ── Desktop Navigation ── */}
        <ul className="navbar-list desktop-nav">
          {isStaff && ESSENTIALS.map(it => (
            <li key={it.path}>
              <Link to={it.path} className={`nav-direct-link ${path === it.path || (it.path === '/calendar' && path.startsWith('/calendar')) ? 'active' : ''}`}>
                {it.icon} {it.label}
              </Link>
            </li>
          ))}

          {isStaff && <DropdownMenu label={OPERATIONS.label} icon={OPERATIONS.icon} items={forRole(OPERATIONS.items)} currentPath={path} />}
          {isStaff && <DropdownMenu label={BUSINESS.label} icon={BUSINESS.icon} items={forRole(BUSINESS.items)} currentPath={path} />}
          {isStaff && <DropdownMenu label={DEVELOPMENT.label} icon={DEVELOPMENT.icon} items={forRole(DEVELOPMENT.items)} currentPath={path} />}
          {userRole === 'admin' && <DropdownMenu {...ADMIN} currentPath={path} />}
          {userRole === 'coach' && (
            <li>
              <Link to="/profile" className={`nav-direct-link ${path === '/profile' ? 'active' : ''}`}>🪪 Profile</Link>
            </li>
          )}

          {/* Parent nav — Home (action-first dashboard) covers "my athletes";
              Payments points at the real invoice view, not the legacy portal. */}
          {userRole === 'parent' && (
            <>
              <li><Link to="/" className={`nav-direct-link ${path === '/' ? 'active' : ''}`}>🏠 Home</Link></li>
              <li><Link to="/calendar" className="nav-direct-link">🗓 Calendar</Link></li>
              <li><Link to="/communication" className="nav-direct-link">💬 Messages</Link></li>
              <li><Link to="/sign-waivers" className="nav-direct-link">📝 Waivers</Link></li>
              <li><Link to="/parent/invoices" className="nav-direct-link">💳 Payments</Link></li>
            </>
          )}

          {/* Athlete nav */}
          {userRole === 'athlete' && (
            <>
              <li><Link to="/athlete/home" className="nav-direct-link">🤸 Home</Link></li>
              <li><Link to="/video" className={`nav-direct-link ${path.startsWith('/video') ? 'active' : ''}`}>🎥 Video</Link></li>
              <li><Link to="/athlete-card/me?tab=performance" className={`nav-direct-link ${path.startsWith('/athlete-card') ? 'active' : ''}`}>📊 My Progress</Link></li>
              <li><Link to="/goals" className="nav-direct-link">🎯 Goals</Link></li>
              <li><Link to="/wellness/checkin" className="nav-direct-link">📝 Check-In</Link></li>
              <li><Link to="/wellness/surveys" className="nav-direct-link">📋 Surveys</Link></li>
              <li><Link to="/athlete-card/me?tab=wellness" className="nav-direct-link">🧘 My Wellness</Link></li>
              <li><Link to="/calendar" className="nav-direct-link">🗓 Calendar</Link></li>
              <li><Link to="/communication" className="nav-direct-link">💬 Announcements</Link></li>
            </>
          )}

          {(userRole === 'superadmin' || userRole === 'staff') && (
            <>
              <DropdownMenu label={MODEL_LAB.label} icon={MODEL_LAB.icon} items={MODEL_LAB.items} currentPath={path} />
              <li>
                <Link to="/superadmin" className="nav-direct-link" style={{ color: '#e67e22', fontWeight: 700 }}>
                  ⚡ RMT Console
                </Link>
              </li>
            </>
          )}
        </ul>

        {/* ── Right side: notifications + logout ── */}
        <div className="navbar-right">
          <NotificationCenter />
          <button className="logout-button desktop-only" onClick={handleLogout}>Logout</button>
          <button className="hamburger-button" onClick={() => setMobileOpen(o => !o)}>
            {mobileOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* ── Mobile menu (grouped) ── */}
        {mobileOpen && (
          <div className="mobile-dropdown">
            {isStaff && (
              <>
                {ESSENTIALS.map(mlink)}
                <div className="mobile-section">Operations</div>
                {OPERATIONS.items.map(mlink)}
                <div className="mobile-section">Business</div>
                {forRole(BUSINESS.items).map(mlink)}
                <div className="mobile-section">Development</div>
                {DEVELOPMENT.items.map(mlink)}
                {userRole === 'admin' && (
                  <>
                    <div className="mobile-section">Admin</div>
                    {ADMIN.items.map(mlink)}
                  </>
                )}
                {userRole === 'coach' && <Link to="/profile" onClick={close}>🪪 My Profile</Link>}
              </>
            )}

            {userRole === 'parent' && (
              <>
                <Link to="/" onClick={close}>🏠 Home</Link>
                <Link to="/calendar" onClick={close}>🗓 Calendar</Link>
                <Link to="/communication" onClick={close}>💬 Messages</Link>
                <Link to="/sign-waivers" onClick={close}>📝 Waivers</Link>
                <Link to="/parent/invoices" onClick={close}>💳 Payments</Link>
                <Link to="/profile" onClick={close}>🪪 My Profile</Link>
              </>
            )}

            {userRole === 'athlete' && (
              <>
                <Link to="/athlete/home" onClick={close}>🤸 Home</Link>
                <Link to="/video" onClick={close}>🎥 Video</Link>
                <Link to="/athlete-card/me?tab=performance" onClick={close}>📊 My Progress</Link>
                <Link to="/goals" onClick={close}>🎯 Goals</Link>
                <Link to="/wellness/checkin" onClick={close}>📝 Check-In</Link>
                <Link to="/wellness/surveys" onClick={close}>📋 Surveys</Link>
                <Link to="/athlete-card/me?tab=wellness" onClick={close}>🧘 My Wellness</Link>
                <Link to="/calendar" onClick={close}>🗓 Calendar</Link>
                <Link to="/communication" onClick={close}>💬 Announcements</Link>
              </>
            )}

            {(userRole === 'superadmin' || userRole === 'staff') && (
              <>
                <div className="mobile-section">Model Lab</div>
                {MODEL_LAB.items.map(mlink)}
                <Link to="/superadmin" onClick={close} style={{ color: '#e67e22' }}>⚡ RMT Console</Link>
              </>
            )}
            <button className="logout-dropdown" onClick={handleLogout}>🚪 Logout</button>
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
